import { twMerge } from 'tailwind-merge'

export const dayScheduleEventColors = [
  'gray',
  'red',
  'pink',
  'orange',
  'yellow',
  'blue',
  'green',
  'purple',
  'transparent',
] as const
export type DayScheduleEventColors = (typeof dayScheduleEventColors)[number]

export const toDayScheduleEventColor = (
  color: unknown,
): DayScheduleEventColors =>
  dayScheduleEventColors.find((c) => c === color) ?? 'gray'

/**
 * Per-color class sets used by the schedule. Missing fields fall back to a
 * sensible default below (e.g. `gray` has no dedicated bg; the title falls
 * back to base-700). Keeping these as data (not if/else) makes adding a new
 * color a one-line change and lets us table-test the color getters.
 */
type ScheduleColorClasses = {
  /**
   * Background classes for the colored event block. Undefined when a color
   * intentionally renders no background (gray, transparent).
   */
  bg?: { base: string; hover: string }
  /** Outline for the colored event block. */
  border: { base: string; hover: string }
  /** Text color for the event title. */
  title: string
  /** Text color for the event start/end time next to the title. */
  time: string
}

const bgBaseClasses =
  'bg-linear-to-br from-base-0/50 hover:mb-0.5 hover:mx-0.5 duration-500'

const defaultColorClasses: ScheduleColorClasses = {
  // Solid bg: gray reads opaque, unlike the transparent ghost
  bg: {
    base: 'bg-base-0 dark:bg-base-900',
    hover: 'hover:bg-base-50 dark:hover:bg-base-800',
  },
  border: {
    base: 'border-base-300 dark:border-base-500',
    hover: ' hover:border-base-300 dark:hover:border-base-500',
  },
  title: 'text-base-700',
  time: 'text-base-500',
}

const scheduleColorClassMap: Record<
  Exclude<DayScheduleEventColors, 'gray'>,
  ScheduleColorClasses
> = {
  blue: {
    bg: {
      base: 'bg-blue-50 dark:bg-blue-800',
      hover: 'hover:bg-blue-100 dark:hover:bg-blue-900',
    },
    border: {
      base: 'border-blue-300 dark:border-blue-500',
      hover: 'hover:border-blue-300 dark:hover:border-blue-500',
    },
    title: 'text-blue-900 dark:text-blue-100',
    time: 'text-blue-500 group-hover:text-blue-700 dark:text-blue-200 dark:group-hover:text-blue-100',
  },
  pink: {
    bg: {
      base: 'bg-pink-50 dark:bg-pink-800',
      hover: 'hover:bg-pink-100 dark:hover:bg-pink-900',
    },
    border: {
      base: 'border-pink-300 dark:border-pink-500',
      hover: ' hover:border-pink-300 dark:hover:border-pink-500',
    },
    title: 'text-pink-700 dark:text-pink-100',
    time: 'text-pink-500 group-hover:text-pink-700 dark:text-pink-200 dark:group-hover:text-pink-100',
  },
  yellow: {
    bg: {
      base: 'bg-yellow-50 dark:bg-yellow-800',
      hover: 'hover:bg-yellow-100 dark:hover:bg-yellow-900',
    },
    border: {
      base: 'border-yellow-300 dark:border-yellow-500',
      hover: ' hover:border-yellow-300 dark:hover:border-yellow-500',
    },
    title: 'text-yellow-700 dark:text-yellow-100',
    time: 'text-yellow-500 group-hover:text-yellow-700 dark:text-yellow-200 dark:group-hover:text-yellow-100',
  },
  orange: {
    bg: {
      base: 'bg-orange-50 dark:bg-orange-800',
      hover: 'hover:bg-orange-100 dark:hover:bg-orange-900',
    },
    border: {
      base: 'border-orange-300 dark:border-orange-500',
      hover: ' hover:border-orange-300 dark:hover:border-orange-500',
    },
    title: 'text-orange-700 dark:text-orange-100',
    time: 'text-orange-500 group-hover:text-orange-700 dark:text-orange-200 dark:group-hover:text-orange-100',
  },
  green: {
    bg: {
      base: 'bg-green-50 dark:bg-green-800',
      hover: 'hover:bg-green-100 dark:hover:bg-green-900',
    },
    border: {
      base: 'border-green-300 dark:border-green-500',
      hover: ' hover:border-green-300 dark:hover:border-green-500',
    },
    title: 'text-green-900 dark:text-green-100',
    time: 'text-green-500 group-hover:text-green-700 dark:text-green-200 dark:group-hover:text-green-100',
  },
  purple: {
    bg: {
      base: 'bg-purple-50 dark:bg-purple-800',
      hover: 'hover:bg-purple-100 dark:hover:bg-purple-900',
    },
    border: {
      base: 'border-purple-300 dark:border-purple-500',
      hover: ' hover:border-purple-300 dark:hover:border-purple-500',
    },
    title: 'text-purple-700 dark:text-purple-100',
    time: 'text-purple-500 group-hover:text-purple-700 dark:text-purple-200 dark:group-hover:text-purple-100',
  },
  red: {
    bg: {
      base: 'bg-red-50 dark:bg-red-800',
      hover: 'hover:bg-red-100 dark:hover:bg-red-900',
    },
    border: {
      base: 'border-red-300 dark:border-red-500',
      hover: ' hover:border-red-300 dark:hover:border-red-500',
    },
    title: 'text-red-900 dark:text-red-100',
    time: 'text-red-500 group-hover:text-red-700 dark:text-red-200 dark:group-hover:text-red-100',
  },
  transparent: {
    // transparent is a "ghost" style (dashed outline, see-through fill); no bg
    border: {
      base: 'border-dashed bg-white border-2 border-gray-400 dark:border-gray-500',
      hover: 'hover:border-primary-400 dark:hover:border-primary-500',
    },
    title:
      'font-semibold text-base-900 group-hover:text-indigo-900 dark:group-hover:text-indigo-100 transition duration-500',
    time: 'text-base-500 group-hover:text-indigo-500 transition duration-500',
  },
}

const getColorClasses = (color: DayScheduleEventColors): ScheduleColorClasses =>
  color === 'gray'
    ? defaultColorClasses
    : scheduleColorClassMap[color] ?? defaultColorClasses

export const getBgColorClasses = (
  color: DayScheduleEventColors,
  disableHighlightOnHover?: boolean,
) => {
  // Only transparent has no bg — its ghost style needs show-through
  const bg = getColorClasses(color).bg
  if (!bg) return undefined
  return twMerge(bgBaseClasses, bg.base, !disableHighlightOnHover && bg.hover)
}

export const getBorderColorClasses = (
  color: DayScheduleEventColors,
  disableHighlightOnHover?: boolean,
) => {
  const { border } = getColorClasses(color)
  return twMerge(border.base, !disableHighlightOnHover && border.hover)
}

export const getTitleColorClasses = (color: DayScheduleEventColors) =>
  getColorClasses(color).title

export const getTimeColorClasses = (color: DayScheduleEventColors) =>
  getColorClasses(color).time
